"use client";

// 헤더의 인증 해제 자리 (#1114).
//
// 로그인·회원가입 아이콘(AuthIconSlot)은 지점이 켜야 보인다(showAuthIcon 기본
// false). 그런데 **인증 해제는 인증된 동안 어디서나 보여야 한다** — 매장
// 태블릿이나 가족 공용 기기에서 앞사람 인증이 남았을 때, 예약 화면까지 들어가야
// 풀 수 있으면 길이 막힌 것과 같다.
//
// 그래서 이 슬롯은 노출 설정과 무관하게 헤더에 늘 놓이고, **인증이 있을 때만**
// 스스로 나타난다. 인증이 없으면 아무것도 그리지 않는다.

import { useEffect, useState } from "react";
import {
  EndIdentityButton,
  hasLocalIdentity,
  IV_CHANGED_EVENT,
} from "../identity-verification/index.jsx";

export function IdentityStatusSlot({
  locale = "ko",
  branchId,
  className = "",
  /* 헤더는 아이콘이다 — 장바구니·메뉴와 나란히 서므로 줄을 맞춘다. */
  size = 20,
}) {
  const id = branchId || (process.env.NEXT_PUBLIC_BRANCH_ID || "").trim();
  const [shown, setShown] = useState(false);

  useEffect(() => {
    let alive = true;

    /* 저장값은 **서버 세션보다 먼저 만료된다.**
     *
     * 저장값 수명은 쓴 시점부터 1시간으로 고정인데, 서버 세션은 화면들이
     * /api/verify/session 을 부를 때마다 갱신된다(빠른상담 바가 모든 페이지에서
     * 부른다). 그래서 한 시간쯤 머무르면 **인증은 살아 있는데 저장값만 사라져**
     * 아이콘이 조용히 없어졌다. 실제로 그렇게 안 보였다.
     *
     * 그래서 서버에도 묻는다. 다만 일반 조회는 접근 쿠키를 갱신하고 지점 훅을
     * 부르므로(헤더는 모든 페이지에 있다 — 세션이 끝나지 않게 된다) 부수효과가
     * 없는 probe 로 묻는다. */
    const sync = () => {
      // 저장값이 있으면 먼저 보여 깜빡임을 없앤다.
      if (hasLocalIdentity(id)) {
        setShown(true);
        return;
      }
      fetch("/api/verify/session?probe=1", { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (alive) setShown(d?.verified === true);
        })
        .catch(() => {});
    };
    sync();

    /* 같은 탭에서 인증을 끝내면 storage 이벤트가 오지 않는다. 그래서 인증
     * 컴포넌트가 직접 쏘는 신호를 함께 듣는다. 다른 탭은 storage 로 온다. */
    window.addEventListener(IV_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    /* 카카오 인증은 외부 페이지를 다녀온다. 돌아왔을 때 다시 본다. */
    window.addEventListener("focus", sync);
    return () => {
      alive = false;
      window.removeEventListener(IV_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, [id]);

  if (!shown) return null;

  return (
    <EndIdentityButton
      variant="icon"
      size={size}
      branchId={id}
      locale={locale}
      className={className}
      onEnded={() => {
        /* 어느 화면에서 눌렀는지 모른다. 예약 조회처럼 인증된 신원으로 이미
         * 내용을 그린 화면이 그대로 남으면 "해제했는데 그대로" 가 된다.
         * 콜백을 화면마다 연결하는 대신 새로 그린다 — 해제는 드물게 일어나고,
         * 서버 세션 삭제가 끝난 뒤이므로 인증 화면이 되살아나지 않는다. */
        if (typeof window !== "undefined") window.location.reload();
      }}
    />
  );
}

export default IdentityStatusSlot;
